import { io, Socket } from 'socket.io-client'
import { createContext, useCallback, useEffect, useRef, useState, ReactNode, useContext } from 'react'

import { config } from '../config'
import { AuthContext } from './auth.context'
import { isSuiteSsoRedirectMode } from '../sso/sso-flags'
import { getSuiteAccessTokenFromIdentity } from '../sso/services/suite-access-token.service'

interface SocketsContextType {
  socket: Socket | null
  appDataSocket: Socket | null
  isSocketInitialized: boolean
  isAppDataSocketInitialized: boolean
  connectExcelReaderSocket: () => void
}

type Message = { id: string; text: string; at: number }

export const SocketsContext = createContext<SocketsContextType | undefined>(undefined)

const Provider = ({ children }: { children: ReactNode }) => {
  const { isAuth } = useContext(AuthContext)
  const socket = useRef<Socket | null>(null)
  const appDataSocket = useRef<Socket | null>(null)
  const socketsInitializedRef = useRef<boolean>(false)

  const [, /* lastPong */ setLastPong] = useState<number | null>(null)
  const [, /* notifications */ setNotifications] = useState<Message[]>([])
  const [isSocketInitialized, setIsSocketInitialized] = useState(false)
  const [isAppDataSocketInitialized, setIsAppDataSocketInitialized] = useState(false)

  const connectExcelReaderSocket = useCallback(() => {
    if (!socket.current) {
      socket.current = io(config.socket.url, {
        secure: config.socket.secure,
        transports: ['polling', 'websocket'],
        reconnectionAttempts: 5,
        autoConnect: false
      })

      socket.current.on('connect', () => {
        setIsSocketInitialized(true)
      })

      socket.current.on('connect_error', () => {
        setIsSocketInitialized(false)
      })
    }

    if (!socket.current.connected) {
      socket.current.connect()
    }
  }, [])

  useEffect(() => {
    const disconnectSockets = () => {
      socket.current?.close()
      socket.current?.disconnect()
      appDataSocket.current?.close()
      appDataSocket.current?.disconnect()
      appDataSocket.current?.removeAllListeners()
      socketsInitializedRef.current = false
      setIsSocketInitialized(false)
      setIsAppDataSocketInitialized(false)
    }

    const connectSockets = async () => {
      if (socketsInitializedRef.current) {
        return
      }

      let authToken = isAuth?.replaceAll('"', '') || ''

      if (!authToken && isSuiteSsoRedirectMode()) {
        if (globalThis.sessionStorage.getItem('suiteDashboardReady') !== 'true') {
          return
        }

        const payRefreshToken =
          globalThis.sessionStorage.getItem('token')?.replaceAll('"', '') ||
          globalThis.localStorage.getItem('token')?.replaceAll('"', '') ||
          ''

        if (payRefreshToken) {
          authToken = payRefreshToken
        } else {
          try {
            authToken = await getSuiteAccessTokenFromIdentity()
          } catch {
            return
          }
        }
      }

      if (!authToken) {
        return
      }

      appDataSocket.current = io(config.appDataSocket.url, {
        transports: ['websocket', 'polling'],
        autoConnect: false,
        auth: { token: authToken }
      })

      appDataSocket.current.on('connect', () => {
        setIsAppDataSocketInitialized(true)
      })

      appDataSocket.current.on('connect_error', (err) => {
        console.error('appDataSocket connect_error', err.message)
      })

      appDataSocket.current.on('lifecycle:connected', (info) => console.log('connected as', info))
      appDataSocket.current.on('lifecycle:disconnected', (info) => console.log('server observed disconnect', info))
      appDataSocket.current.on('server:pong', (p: { ts: number; at: number }) => {
        setLastPong(p.at)
      })
      appDataSocket.current.on('private:notification', (m: Message) => setNotifications((prev) => [m, ...prev]))
      appDataSocket.current.on('private:update', (data) => console.log('private update', data))

      appDataSocket.current.connect()
      socketsInitializedRef.current = true
    }

    const onSuiteDashboardReady = () => {
      void connectSockets()
    }

    void connectSockets()

    globalThis.addEventListener('suite-dashboard-ready', onSuiteDashboardReady)

    const legacyPaySession = Boolean(isAuth?.replaceAll('"', '').trim())
    const suiteSession = isSuiteSsoRedirectMode() && globalThis.sessionStorage.getItem('suiteDashboardReady') === 'true'

    if (!legacyPaySession && !suiteSession && socketsInitializedRef.current) {
      disconnectSockets()
    }

    return () => {
      globalThis.removeEventListener('suite-dashboard-ready', onSuiteDashboardReady)
      disconnectSockets()
    }
  }, [isAuth])

  return (
    <SocketsContext.Provider
      value={{
        socket: socket.current,
        isSocketInitialized,
        appDataSocket: appDataSocket.current,
        isAppDataSocketInitialized,
        connectExcelReaderSocket
      }}
    >
      {children}
    </SocketsContext.Provider>
  )
}

const SocketsContextProvider = {
  Provider,
  Consumer: SocketsContext.Consumer
}

export default SocketsContextProvider
